import React, { useMemo, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getComponent }   from './ComponentRegistry';
import { DynamicSwiperSlot, usePageSwiperSlots } from '../../dynamicSwipers/PageDynamicSwipers';
import { resolveSwiperPage, SLOT_TOP, SLOT_BOTTOM, slotAfter } from '../../dynamicSwipers/dynamicSwiperRegistry';
import {
    getPageSections,
    shouldRenderSection,
    transformSectionData,
    getSection,
    SECTIONS_LIBRARY,
} from './pagesConfig';

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANT — sections indépendantes (reçoivent paginationProps + gridRef_)
// ═══════════════════════════════════════════════════════════════════════════

export const INDEPENDENT_SECTION_IDS = new Set([
    'productGrid',
    'userViewGrid',
    'bestSellersGrid',
    'dealTodaysGrid',
    'shopProductGrid',
]);


// ═══════════════════════════════════════════════════════════════════════════
// HELPER — safeScroll
// ═══════════════════════════════════════════════════════════════════════════

const safeScroll = (ref, options = { behavior: 'smooth' }) => {
    if (ref && ref.current && document.body.contains(ref.current)) {
        setTimeout(() => {
            if (ref.current && document.body.contains(ref.current)) {
                ref.current.scrollIntoView(options);
            }
        }, 0);
    }
};

  const scrollToParagraph = (ref) => {
         if (ref && ref.current) {
    ref.current.scrollIntoView({ behavior: 'smooth' });
  }
      };

// ═══════════════════════════════════════════════════════════════════════════
// HELPERs internes
// ═══════════════════════════════════════════════════════════════════════════

const buildSectionsFromFacets = (sectionIds) =>
    sectionIds
        .map((sId, index) => {
            const baseSection = SECTIONS_LIBRARY[sId];
            if (!baseSection) {
                console.warn(`[DynamicPageRenderer] Section "${sId}" not found`);
                return null;
            }
            return { ...baseSection, order: index, enabled: true, props: { ...baseSection.defaultProps } };
        })
        .filter(Boolean);

const applyConfigOverrides = (section, override) => {
    if (!override) return section;
    let result = { ...section };
    if (override.props && Object.keys(override.props).length > 0) {
        result.props = { ...result.props, ...override.props };
    }
    if (override.overrides && Object.keys(override.overrides).length > 0) {
        const ov = override.overrides;
        if (ov.component !== undefined) result.component = ov.component;
        if (ov.facetKey  !== undefined) result.facetKey  = ov.facetKey;
        if (ov.dataKey   !== undefined) result.dataKey   = ov.dataKey;
        if (ov.skip  !== undefined && ov.skip  !== null) result.props = { ...result.props, skip:  ov.skip  };
        if (ov.limit !== undefined && ov.limit !== null) result.props = { ...result.props, limit: ov.limit };
    }
    return result;
};

const buildComponentProps = ({ sectionConfig, data, initialData, sectionProps, context }) => {
    const baseProps = {
        infoSource: context.infoSource,
        page: context?.page,
        totalKey: context?.totalKey,
        myFilters: context?.myFilters,
        categoryId: context?.categoryContext?.categoryId,
        initialData,
        // ProductGridPage must know which configured section instantiated it.
        // Without this identity, userViewGrid fell back to the page's generic
        // productGrid loader and displayed the wrong products.
        sectionId: sectionConfig.id,
        facet: sectionConfig.facetKey,
        ...sectionProps,
    };
    if (sectionConfig.adapterProps) {
        return { ...baseProps, ...sectionConfig.adapterProps({ data, sectionProps, context }) };
    }
    return { ...baseProps, data };
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT — SectionRenderer
// ═══════════════════════════════════════════════════════════════════════════

// const SectionRenderer = ({
//     sectionConfig, pageData, infoSource, laguangeElakay,
//     homeStyles, nbrArr, categoryContext, paginationProps,
//     reloadFacets, facetTitle = false, page, myFilters
// }) => {
//     const { id, component: componentName, props: sectionProps } = sectionConfig;
//     const Component = getComponent(componentName);

//     const resolvedPaginationProps = useMemo(() => {
//         if (!INDEPENDENT_SECTION_IDS.has(id)) return null;
//         if (!paginationProps) return null;

//         const sectionSkip        = paginationProps.getSectionSkip?.(id)        ?? sectionProps?.skip  ?? 0;
//         const sectionSize        = paginationProps.getSectionSize?.(id)        ?? paginationProps.size ?? 0;
//         const sectionSiseProduct = paginationProps.getSectionSiseProduct?.(id) ?? paginationProps.siseProduct ?? 0;

//         return {
//             skip:        sectionSkip,
//             limit:       sectionProps?.limit ?? paginationProps.limit ?? 12,
//             size:        sectionSize,
//             siseProduct: sectionSiseProduct,
//             loadMore:    paginationProps.loadMore,
//             loadMore0:   paginationProps.loadMore0,
//             sectionId:   id,
//             facetKey:    sectionConfig.facetKey,
//             dataKey:     sectionConfig.dataKey,
//         };
//     }, [id, paginationProps, sectionProps, sectionConfig.facetKey, sectionConfig.dataKey]);

//     if (!Component) {
//         console.error(`[SectionRenderer] Component "${componentName}" not found for section "${id}"`);
//         return null;
//     }

//     const data    = transformSectionData(sectionConfig, pageData);
//     const context = {
//         infoSource, laguangeElakay, homeStyles, nbrArr, categoryContext, page, myFilters,
//         paginationProps: resolvedPaginationProps,
//         reloadFacets,
//         loadProductsByArrival: reloadFacets,
//     };
//     return (
//         <div
//             data-section-id={id}
//             data-section-order={sectionConfig.order}
//             className={`${!facetTitle ? 'dynamic-section-with-title' : 'dynamic-section'} section-${id}`}
//         >
//             <Component {...buildComponentProps({ sectionConfig, data, sectionProps, context })} />
//         </div>
//     );
// };

const SectionRenderer = ({
    sectionConfig, pageData, infoSource, laguangeElakay,
    homeStyles, nbrArr, categoryContext, paginationProps,
    reloadFacets, facetTitle = false, page, totalKey, myFilters
}) => {
    const { id, component: componentName, props: sectionProps } = sectionConfig;
    const Component = getComponent(componentName);

    const resolvedPaginationProps = useMemo(() => {
        if (!INDEPENDENT_SECTION_IDS.has(id)) return null;
        if (!paginationProps) return null;

        const sectionSkip        = paginationProps.getSectionSkip?.(id)        ?? sectionProps?.skip  ?? 0;
        const sectionSize        = paginationProps.getSectionSize?.(id)        ?? paginationProps.size ?? 0;
        const sectionSiseProduct = paginationProps.getSectionSiseProduct?.(id) ?? paginationProps.siseProduct ?? 0;

        return {
            skip:        sectionSkip,
            limit:       sectionProps?.limit ?? paginationProps.limit ?? 12,
            size:        sectionSize,
            siseProduct: sectionSiseProduct,
            loadMore:    paginationProps.loadMore,
            loadMore0:   paginationProps.loadMore0,
            sectionId:   id,
            facetKey:    sectionConfig.facetKey,
            dataKey:     sectionConfig.dataKey,
        };
    }, [id, paginationProps, sectionProps, sectionConfig.facetKey, sectionConfig.dataKey]);

    if (!Component) {
        console.error(`[SectionRenderer] Component "${componentName}" not found for section "${id}"`);
        return null;
    }

    // ── selfLoading : le composant charge ses propres données ────────────
    // data = null → ProductGridPage / ProductGridShop ignorent cette prop
    // et utilisent leur propre useEffect + state interne
    const data = sectionConfig.selfLoading
        ? null
        : transformSectionData(sectionConfig, pageData);

    const context = {
        infoSource, laguangeElakay, homeStyles, nbrArr, categoryContext, page, totalKey, myFilters,
        paginationProps: resolvedPaginationProps,
        reloadFacets,
        loadProductsByArrival: reloadFacets,
        pageData,
    };

    // For selfLoading sections, the base facet the parent's useDataLoader
    // already fetched (pageData[sectionConfig.dataKey]) is handed down as
    // `initialData` instead of being silently discarded — lets the
    // section seed its first render from it and skip its own redundant
    // fetch on mount. Sections without a matching pageData key (or pages
    // where useDataLoader never fetched this facet) simply get
    // `undefined` and fall back to self-fetching exactly as before.
    const initialData = sectionConfig.selfLoading && sectionConfig.dataKey && pageData
        ? {
            [sectionConfig.dataKey]: pageData[sectionConfig.dataKey],
            size: pageData.size,
            siseProduct: pageData.siseProduct,
        }
        : undefined;

    return (
        <div
            data-section-id={id}
            data-section-order={sectionConfig.order}
            className={`${!facetTitle ? 'dynamic-section-with-title' : 'dynamic-section'} section-${id}`}
        >
            <Component {...buildComponentProps({ sectionConfig, data, initialData, sectionProps, context })} />
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL — DynamicPageRenderer
// ═══════════════════════════════════════════════════════════════════════════
//
// DEUX REFS — rôles distincts:
//
// ┌──────────────────────────────────────────────────────────────────────┐
// │ pageTopRef  — TÊTE DE L'ENTÊTE (tout premier élément du Layout)      │
// │                                                                      │
// │  Fourni par ProductCatalog:                                          │
// │    <div ref={pageTopRef} style={infoSource.styleNav}>                │
// │      <DynamicPageRenderer pageTopRef={pageTopRef} ... />             │
// │                                                                      │
// │  Déclenché: useEffect([facets, pageId])                              │
// │    → URL change / nouvelle catégorie → safeScroll(pageTopRef)        │
// │    → L'utilisateur remonte au tout début du contenu (avant sliders)  │
// │                                                                      │
// │  Fallback interne: si non fourni → internalTopRef sur un div vide    │
// │    au début du render de DynamicPageRenderer.                        │
// └──────────────────────────────────────────────────────────────────────┘
//
// ┌──────────────────────────────────────────────────────────────────────┐
// │ gridRef_  — HAUT DU COMPOSANT ProductGridPage                         │
// │             (première section dans INDEPENDENT_SECTION_IDS)          │
// │                                                                      │
// │  Fourni par ProductCatalog:                                          │
// │    const gridRef_ = useRef(null);                                     │
// │    buildSectionPaginationProps({ scrollToRef: gridRef_, ... })        │
// │    <DynamicPageRenderer gridRef_={gridRef_} ... />                     │
// │                                                                      │
// │  Attaché ici: sur le <div> wrapper de la première section            │
// │    indépendante dans le render.                                      │
// │    Ce div est le parent direct de <SectionRenderer> → ProductGridPage│
// │                                                                      │
// │  Déclenché: buildSectionPaginationProps.loadMore/loadMore0           │
// │    → après clic pagination → safeScroll(gridRef_)                     │
// │    → scroll vers le HAUT de la grille, pas le haut de la page.       │
// │                                                                      │
// │  Double sécurité: ProductGridPage.handleLoadMore scroll aussi via    │
// │    son divRef interne (même noeud cible → pas de conflit).           │
// │                                                                      │
// │  Fallback: buildSectionPaginationProps utilise querySelector         │
// │    [data-grid-section="sectionId"] si scrollToRef non fourni.        │
// └──────────────────────────────────────────────────────────────────────┘
//
// RÉSULTAT DANS LE DOM:
//
//   ProductCatalog:
//   <div ref={pageTopRef}>          ← 1er élément du Layout, avant tout
//     <div ref={gridRef_} />         ← div invisible juste avant DPR (fallback)
//     <DynamicPageRenderer
//       pageTopRef={pageTopRef}
//       gridRef_={gridRef_}
//     >
//       <!-- ancre fallback si pageTopRef non fourni -->
//       <div id="dynamic-page-top" />
//
//       <!-- sections non-indépendantes: sliders, matrices... -->
//       <SectionRenderer id="elakayCategories" />
//       <SectionRenderer id="historySearchMatrix" />
//       <SectionRenderer id="followShopUser" />
//
//       <!-- première section indépendante: gridRef_ attaché ici -->
//       <div ref={gridRef_} data-grid-section="bestSellersGrid">
//         <SectionRenderer id="bestSellersGrid" />  ← ProductGridPage
//       </div>
//     </DynamicPageRenderer>
//   </div>

const DynamicPageRenderer = ({
    facets          = null,
    pageId          = 'HOME',
    pageData,
    infoSource,
    page,
    totalKey,
    myFilters,
    laguangeElakay,
    homeStyles,
    nbrArr,
    categoryContext = null,
    paginationProps = null,
    reloadFacets,
    configOverrides = {},
    excludeSections = [],
    facetTitle      = false,
    gridRef         = null,   // ← haut de ProductGridPage (ProductCatalog)
    // Dynamic Swipers (Family Swiper Manager + Animation Settings): which
    // page's promotional swipers to insert between the sections. Omitted ->
    // derived from pageId when it is a registered main page; category pages
    // pass { pageType: 'category', categoryId }; false -> none.
    swiperPage      = undefined,
}) => {
    // ── Ref interne fallback pour gridRef_ ─────────────────────────────────
    const internalGridRef = useRef(null);
    const gRef = gridRef || internalGridRef;

    // ── Scroll haut de page au changement d'URL ──────────────────────────
    // useLocation().pathname capte TOUT changement d'URL (catégorie, page, query).
    // categoryId couvre params?.categoryId || props.categoryId.
    // behavior: 'instant' → retour immédiat sans animation — plus réactif
    // qu'un smooth qui traine sur les longues pages.
    const { pathname } = useLocation();
    const categoryId   = categoryContext?.categoryId ?? null;

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, [categoryId, pathname]);

    const pageSections = useMemo(() => {
        // `[]` is a valid, explicitly saved empty page. Only `null`/an
        // omitted facets prop means that the registry default should apply.
        let sections = Array.isArray(facets)
            ? buildSectionsFromFacets(facets)
            : getPageSections(pageId);

        if (Object.keys(configOverrides).length > 0) {
            sections = sections.map(s => applyConfigOverrides(s, configOverrides[s.id]));
        }
        if (excludeSections.length > 0) {
            sections = sections.filter(s => !excludeSections.includes(s.id));
        }
        return sections;
    }, [facets, pageId, configOverrides, excludeSections]);

    // ── Première section indépendante → reçoit gRef_ ───────────────────────
    const firstIndependentId = useMemo(
        () => pageSections.find(s => INDEPENDENT_SECTION_IDS.has(s.id))?.id || null,
        [pageSections]
    );

    // ── Dynamic Swipers: one cached request per page, grouped by slot ─────
    // Slots are keyed by section id ("after:<sectionId>"), never by DOM
    // index, so reordering sections in Animation Settings carries the
    // swipers along. A slot whose section left the page renders at the end.
    const swiperPageKey = swiperPage === false ? 'none'
        : `${swiperPage?.pageType || ''}|${swiperPage?.pageKey || ''}|${swiperPage?.categoryId || ''}|${pageId}`;
    const swiperDescriptor = useMemo(
        () => resolveSwiperPage(swiperPage, pageId),
        // swiperPageKey is the stable identity of swiperPage + pageId.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [swiperPageKey]
    );
    const sectionIds = useMemo(() => pageSections.map(s => s.id), [pageSections]);
    const swiperSlots = usePageSwiperSlots(swiperDescriptor, sectionIds);
    const endOfPageSwipers = useMemo(
        () => [...swiperSlots.orphans, ...(swiperSlots.bySlot.get(SLOT_BOTTOM) || [])],
        [swiperSlots]
    );

    return (
        <>
            <DynamicSwiperSlot
                slotKey={SLOT_TOP}
                placements={swiperSlots.bySlot.get(SLOT_TOP)}
                firstId={swiperSlots.firstId}
            />
            {pageSections.map(sectionConfig => {
                // Always [section-or-null, slot]: the slot keeps its position
                // (and its Swiper instance) while the section's data loads.
                const afterSlot = (
                    <DynamicSwiperSlot
                        slotKey={slotAfter(sectionConfig.id)}
                        placements={swiperSlots.bySlot.get(slotAfter(sectionConfig.id))}
                    />
                );
                if (!shouldRenderSection(sectionConfig, pageData)) {
                    return (
                        <React.Fragment key={sectionConfig.id}>
                            {null}
                            {afterSlot}
                        </React.Fragment>
                    );
                }

                const isFirstIndependent = sectionConfig.id === firstIndependentId;

                // ── Première section indépendante: wrapper avec gRef_ ───────
                // gRef_.current = ce div → haut de ProductGridPage dans le DOM.
                // buildSectionPaginationProps.scrollToGrid scroll vers lui.
                // ProductGridPage scroll aussi via son propre divRef interne.
                if (isFirstIndependent) {
                    return (
                        <React.Fragment key={sectionConfig.id}>
                        <div
                            // ref={gRef}
                            data-grid-section={sectionConfig.id}
                            style={{ position: 'relative' }}
                        >
                            <SectionRenderer
                                sectionConfig={sectionConfig}
                                pageData={pageData}
                                infoSource={infoSource}
                                laguangeElakay={laguangeElakay}
                                homeStyles={homeStyles}
                                facetTitle={facetTitle}
                                nbrArr={nbrArr}
                                categoryContext={categoryContext}
                                paginationProps={paginationProps}
                                reloadFacets={reloadFacets}
                                page={page}
                                totalKey={totalKey}
                                myFilters={myFilters}
                            />
                        </div>
                        {afterSlot}
                        </React.Fragment>
                    );
                }

                // ── Sections non-indépendantes ─────────────────────────────
                return (
                    <React.Fragment key={sectionConfig.id}>
                    <SectionRenderer
                        sectionConfig={sectionConfig}
                        pageData={pageData}
                        infoSource={infoSource}
                        laguangeElakay={laguangeElakay}
                        homeStyles={homeStyles}
                        facetTitle={facetTitle}
                        nbrArr={nbrArr}
                        categoryContext={categoryContext}
                        paginationProps={paginationProps}
                        reloadFacets={reloadFacets}
                        page={page}
                        totalKey={totalKey}
                        myFilters={myFilters}   
                    />
                    {afterSlot}
                    </React.Fragment>
                );
            })}
            <DynamicSwiperSlot slotKey={SLOT_BOTTOM} placements={endOfPageSwipers} />
        </>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT — SingleSectionRenderer
// ═══════════════════════════════════════════════════════════════════════════

export const SingleSectionRenderer = ({
    sectionId, pageData, customProps = {}, configOverride = null,
    facetTitle = false, ...props
}) => {
    const baseSection = getSection(sectionId);
    if (!baseSection) {
        console.warn(`[SingleSectionRenderer] Section "${sectionId}" not found`);
        return null;
    }

    let sectionConfig = {
        ...baseSection,
        order: 0,
        props: { ...baseSection.defaultProps, ...customProps },
    };
    if (configOverride) sectionConfig = applyConfigOverrides(sectionConfig, configOverride);
    if (!shouldRenderSection(sectionConfig, pageData)) return null;

    return <SectionRenderer sectionConfig={sectionConfig} pageData={pageData} facetTitle={facetTitle} {...props} />;
};

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT — buildSectionPaginationProps  v5.3.1
// ═══════════════════════════════════════════════════════════════════════════
//
// scrollToRef = gridRef_ fourni par ProductCatalog.
//   Pointe sur le <div ref={gRef_}> wrapper de la première section
//   indépendante (attaché dans DynamicPageRenderer ci-dessus).
//   → loadMore/loadMore0 scrollent vers le HAUT DE ProductGridPage.
//   → Fallback querySelector[data-grid-section] si scrollToRef null.
//
// pageTopRef → géré séparément via DynamicPageRenderer useEffect.
//   → scroll tête de l'entête au changement d'URL.

export const buildSectionPaginationProps = ({
    pageData,
    setPageData,
    limit        = 12,
    reloadFacets,
    scrollToRef  = null,  // gridRef_ → wrapper de la première section indépendante
}) => {

    // ── scrollToGrid — scroll sécurisé vers la grille ─────────────────────
    const scrollToGrid = (sectionId) => {
        if (scrollToRef && scrollToRef.current && document.body.contains(scrollToRef.current)) {
            setTimeout(() => {
                if (scrollToRef.current && document.body.contains(scrollToRef.current)) {
                    scrollToRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 50);
            return;
        }
        // Fallback DOM
        if (sectionId) {
            const anchor = document.querySelector(`[data-grid-section="${sectionId}"]`);
            if (anchor && document.body.contains(anchor)) {
                setTimeout(() => {
                    if (document.body.contains(anchor)) {
                        anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }, 50);
            }
        }
    };

    // ── Helpers skip par section ──────────────────────────────────────────

    const getSectionSkip = (sectionId) =>
        pageData?.__pagination__?.[sectionId]?.skip ?? 0;

    const getSectionSize = (sectionId) =>
        pageData?.__pagination__?.[sectionId]?.size ?? pageData?.size ?? 0;

    const getSectionSiseProduct = (sectionId) =>
        pageData?.__pagination__?.[sectionId]?.siseProduct ?? pageData?.siseProduct ?? 0;

    const setSectionSkip = (sectionId, newSkip) => {
        if (!setPageData) return;
        setPageData(prev => ({
            ...prev,
            __pagination__: {
                ...(prev.__pagination__ || {}),
                [sectionId]: { ...(prev.__pagination__?.[sectionId] || {}), skip: newSkip },
            },
        }));
    };

    const loadMore = ({ sectionId } = {}) => {
        if (!sectionId) return;
        scrollToParagraph(scrollToRef);
        setSectionSkip(sectionId, getSectionSkip(sectionId) + limit);
        scrollToGrid(sectionId);
        if (reloadFacets) reloadFacets([sectionId]);
    };

    const loadMore0 = ({ sectionId } = {}) => {
        if (!sectionId) return;
        scrollToParagraph(scrollToRef);
        setSectionSkip(sectionId, Math.max(0, getSectionSkip(sectionId) - limit));
        scrollToGrid(sectionId);
        if (reloadFacets) reloadFacets([sectionId]);
    };

    return {
        size:        pageData?.size        ?? 0,
        siseProduct: pageData?.siseProduct ?? 0,
        limit,
        loadMore,
        loadMore0,
        getSectionSkip,
        getSectionSize,
        getSectionSiseProduct,
    };
};

export default DynamicPageRenderer;
